import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MessageCircle, Check, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useSettings } from '../contexts/SettingsContext';
import { useProducts } from '../contexts/ProductsContext';
import { cloudinaryUrl, cloudinarySrcSet } from '../lib/cloudinary';
import ProductCard from '../components/ProductCard';

const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }
function imgUrl(u, w) { return cloudinaryUrl(resolveUrl(u), { width: w }); }

/* ── Image Gallery ──────────────────────────────────────────────────── */
function ImageGallery({ product }) {
  const allImages = React.useMemo(() => {
    const list = [];
    if (product.image_url) list.push(product.image_url);
    (product.images || []).forEach(img => { if (img && img !== product.image_url) list.push(img); });
    return list.length ? list : [''];
  }, [product]);

  const [active,   setActive]   = useState(0);
  const [zoomed,   setZoomed]   = useState(false);
  const [zoomPos,  setZoomPos]  = useState({ x: 50, y: 50 });
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  useEffect(() => { setActive(0); setZoomed(false); }, [product.id]);

  // Preload all images so swipe is instant
  useEffect(() => {
    allImages.forEach(url => {
      const img = new window.Image();
      img.src = imgUrl(url, 800);
    });
  }, [allImages]);

  const prev = e => { e?.stopPropagation(); setActive(i => (i - 1 + allImages.length) % allImages.length); };
  const next = e => { e?.stopPropagation(); setActive(i => (i + 1) % allImages.length); };

  useEffect(() => {
    const h = e => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') setZoomed(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  const onTouchStart = e => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; };
  const onTouchEnd = e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) { dx < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  const handleMouseMove = e => {
    if (!zoomed) return;
    const r = e.currentTarget.getBoundingClientRect();
    setZoomPos({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };

  const isOOS = product.is_out_of_stock === true;

  return (
    <div className="flex flex-col gap-3">
      {/* Main image */}
      <div
        className="relative overflow-hidden bg-[#E8E1D8] select-none"
        style={{ aspectRatio: '3/4', cursor: zoomed ? 'zoom-out' : 'zoom-in' }}
        onClick={() => !isOOS && setZoomed(z => !z)}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setZoomed(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={imgUrl(allImages[active], 900)}
          srcSet={cloudinarySrcSet(resolveUrl(allImages[active]), [600, 900, 1200])}
          sizes="(max-width:768px) 100vw, 50vw"
          alt={`${product.name} view ${active + 1}`}
          data-testid="product-detail-image"
          loading="eager"
          fetchpriority="high"
          decoding="async"
          className={`w-full h-full object-cover ${isOOS ? 'grayscale opacity-70' : ''}`}
          style={zoomed ? { transform: `scale(2.4)`, transformOrigin: `${zoomPos.x}% ${zoomPos.y}%`, transition: 'transform 0.2s ease' } : { willChange: 'transform' }}
          draggable={false}
        />

        {/* OOS overlay */}
        {isOOS && (
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-4 pointer-events-none">
            <div className="w-full bg-[#1E1C1A]/80 backdrop-blur-[2px] py-3 flex flex-col items-center gap-0.5">
              <span className="text-[#F6F3EE] text-[10px] font-medium uppercase tracking-[0.25em]">Out of Stock</span>
              <span className="text-[#C6A978] text-[9px] font-light tracking-[0.18em] uppercase">This piece is gone. ✦</span>
            </div>
          </div>
        )}

        {/* Zoom hint — desktop only */}
        {!zoomed && !isOOS && (
          <div className="absolute bottom-3 right-3 hidden md:flex items-center gap-1 bg-white/75 px-2 py-1 pointer-events-none">
            <ZoomIn size={10} className="text-[#3A3632]" />
            <span className="text-[9px] tracking-widest uppercase text-[#3A3632]">Zoom</span>
          </div>
        )}

        {allImages.length > 1 && (
          <>
            {/* Arrows — desktop only */}
            <button onClick={prev} aria-label="Previous" className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 items-center justify-center hover:bg-white transition-colors z-10"><ChevronLeft size={16} className="text-[#3A3632]" /></button>
            <button onClick={next} aria-label="Next"     className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/80 items-center justify-center hover:bg-white transition-colors z-10"><ChevronRight size={16} className="text-[#3A3632]" /></button>
            {/* Dots */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
              {allImages.map((_, i) => (
                <button key={i} onClick={e => { e.stopPropagation(); setActive(i); }}
                  className={`h-1.5 rounded-full transition-all duration-200 ${i === active ? 'bg-[#C6A978] w-4' : 'bg-white/60 w-1.5'}`} />
              ))}
            </div>
            {/* Counter */}
            <div className="absolute top-3 left-3 bg-black/50 text-white text-[9px] tracking-widest uppercase px-2 py-1 z-10">{active + 1} / {allImages.length}</div>
            {/* Mobile swipe hint */}
            <div className="md:hidden absolute bottom-10 left-1/2 -translate-x-1/2 z-10 pointer-events-none">
              <div className="flex items-center gap-1 bg-black/40 text-white text-[9px] tracking-widest uppercase px-2 py-1">
                <ChevronLeft size={9} /><span>swipe</span><ChevronRight size={9} />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {allImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {allImages.map((img, i) => (
            <button key={i} onClick={() => setActive(i)}
              className={`flex-shrink-0 w-16 h-16 md:w-20 md:h-20 overflow-hidden border-2 transition-all ${i === active ? 'border-[#C6A978]' : 'border-transparent opacity-55 hover:opacity-90'}`}>
              <img src={imgUrl(img, 200)} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" loading="eager" decoding="async" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Main Page ──────────────────────────────────────────────────────── */
export default function ProductDetailPage() {
  const { productId } = useParams();
  const { waLink } = useSettings();
  const { getProduct, products } = useProducts();

  // Instant — from static cache, no API wait
  const product = getProduct(productId);

  // Related — filter from already-loaded products
  const related = React.useMemo(() => {
    if (!product) return [];
    return products
      .filter(p => p.collection === product.collection && p.id !== productId && p.is_visible !== false)
      .slice(0, 3);
  }, [products, product, productId]);

  // Skeleton while product context initialises (should be <50ms)
  if (!product) return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#F6F3EE]">
      <div className="max-w-7xl mx-auto px-0 md:px-12 lg:px-24 pb-4 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-16">
          <div className="aspect-[3/4] bg-[#E8E1D8] relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skeleton-shimmer" />
          </div>
          <div className="px-4 md:px-0 pt-6 flex flex-col gap-4">
            {[60,40,30,100,100].map((w, i) => (
              <div key={i} className="h-6 bg-[#E8E1D8] rounded-sm overflow-hidden relative" style={{ width: `${w}%` }}>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skeleton-shimmer" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const isOOS = product.is_out_of_stock === true;
  const discount = product.mrp && product.mrp > product.price
    ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  return (
    <main data-testid="product-detail-page" className="pt-20 min-h-screen bg-[#F6F3EE]">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 py-3 md:py-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#7A7369]">
          <Link to="/" className="hover:text-[#3A3632] transition-colors">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#3A3632] transition-colors">Shop</Link>
          <span>/</span>
          <span className="text-[#3A3632] truncate max-w-[120px] md:max-w-none">{product.name}</span>
        </div>
      </div>

      {/* Main grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-12 lg:px-24 pb-12 md:pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16">

          <ImageGallery product={product} />

          {/* Info panel */}
          <div className="flex flex-col py-2 md:py-4">
            <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.25em] text-[#C6A978] mb-2">{product.category || 'Jewellery'}</p>

            <h1 data-testid="product-detail-name"
              className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl font-light tracking-tighter text-[#1E1C1A] leading-[1.1] mb-4">
              {product.name}
            </h1>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-1" data-testid="product-detail-price">
              <span className="text-xl md:text-2xl font-light text-[#1E1C1A]">&#8377;{product.price}</span>
              {product.mrp && product.mrp > product.price && (
                <>
                  <span className="text-sm text-[#7A7369] line-through">&#8377;{product.mrp}</span>
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5">{discount}% off</span>
                </>
              )}
            </div>
            {product.mrp && product.mrp > product.price && (
              <p className="text-[10px] text-[#7A7369] mb-4">You save &#8377;{product.mrp - product.price}</p>
            )}

            {/* Trust line */}
            {!isOOS && (
              <p data-testid="product-trust-line" className="text-[11px] font-light text-[#7A7369] tracking-wide mb-5 border-l-2 border-[#C6A978] pl-3 py-1">
                Only 1 piece &middot; No restocks &middot; Ships in 2–3 days &middot; 7-day returns
              </p>
            )}

            {/* CTAs */}
            {!isOOS ? (
              <>
                <a href={waLink(`Hi! I want to order *${product.name}* (₹${product.price}). Is it available?`)}
                  target="_blank" rel="noopener noreferrer" data-testid="product-reserve-btn"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3.5 text-[10px] md:text-xs tracking-[0.2em] uppercase hover:bg-[#1ea855] transition-colors mb-3">
                  <MessageCircle size={14} /> Order on WhatsApp — &#8377;{product.price}
                </a>
                <a href={waLink(`Hi! I have a question about *${product.name}*`)}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-[#DDD7CE] text-[#3A3632] px-6 py-3 text-[10px] md:text-xs tracking-[0.2em] uppercase hover:border-[#3A3632] transition-colors mb-6">
                  Ask a Question
                </a>
              </>
            ) : (
              <div className="border border-[#DDD7CE] px-6 py-4 mb-6 text-center">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#7A7369] mb-1">Out of Stock</p>
                <p className="text-[9px] text-[#C6A978] tracking-widest uppercase">This piece is gone. ✦</p>
              </div>
            )}

            {/* Description */}
            <p data-testid="product-detail-desc"
              className="text-xs md:text-sm font-light leading-relaxed tracking-wide text-[#7A7369] mb-6">
              {product.description}
            </p>

            {/* Features */}
            {product.features?.length > 0 && (
              <ul className="mb-6 space-y-2" data-testid="product-features">
                {product.features.map((feat, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs font-light text-[#3A3632]">
                    <Check size={12} className="text-[#C6A978] flex-shrink-0" /> {feat}
                  </li>
                ))}
              </ul>
            )}

            {/* Delivery info */}
            <div className="border border-[#DDD7CE] p-4 mb-6 space-y-2">
              {[
                ['🚚', 'Free shipping on orders above ₹499'],
                ['📦', 'Delivered in signature Kiraq box with ribbon & card'],
                ['↩', '7-day returns — unworn & in original packaging'],
                ['✨', 'Anti-tarnish gold finish stainless steel'],
                ['🔒', 'Order & pay securely via WhatsApp + UPI'],
              ].map(([icon, text]) => (
                <div key={text} className="flex items-center gap-2 text-xs font-light text-[#7A7369]">
                  <span>{icon}</span> {text}
                </div>
              ))}
            </div>

            {/* Why KIRAQ */}
            <div data-testid="product-why-kiraq" className="border-t border-[#DDD7CE] pt-5">
              <h3 className="font-['Cormorant_Garamond'] text-base md:text-lg font-medium text-[#1E1C1A] mb-3">Why KIRAQ</h3>
              <div className="space-y-2 text-xs font-light text-[#7A7369]">
                <p>Anti-tarnish stainless steel with gold finish — built to last everyday wear.</p>
                <p>Designed with intention — every curve, every weight is deliberate.</p>
                <p>Luxury shouldn't require compromise — exceptional quality at honest prices.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related products */}
      {related.length > 0 && (
        <section className="py-10 md:py-16 px-4 md:px-12 lg:px-24 bg-[#E8E1D8]">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-['Cormorant_Garamond'] text-lg md:text-2xl font-light tracking-tight text-[#1E1C1A] mb-6 text-center">
              You Might Also Like
            </h2>
            <div className="grid grid-cols-3 gap-3 md:gap-8">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i + 4} thumbWidth={600}
                  sizes="(max-width:768px) 33vw, 380px" showWA={false}
                  data-testid={`related-product-${i}`} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}